/**
 * Every string the softphone speaks and the region it speaks it in, so a test
 * or a QA pass can diff against one file. Hardcoded EN, like the rest of the
 * softphone copy — there is no i18n layer.
 */
import type { AnnouncementPoliteness } from 'Services/announcementService';

/**
 * What an announcement is *about*.
 *
 * Two messages sharing a topic are two readings of one piece of state, so
 * inside the debounce window the later replaces the earlier — Hold then Resume
 * announces only "Call resumed". Two messages on different topics are steps of
 * a sequence and both are owed: "Merging calls" is not corrected by
 * "Conference call with 2 participants", it is followed by it.
 */
export type SoftphoneAnnouncementTopic =
  | 'call'
  | 'hold'
  | 'mute'
  | 'connection'
  | 'merge'
  | 'conference';

export interface SoftphoneAnnouncement {
  message: string;
  politeness: AnnouncementPoliteness;
  /** Omitted only by announcements that never reach `AnnouncementService`. */
  topic?: SoftphoneAnnouncementTopic;
}

/**
 * `assertive` is reserved for the two connection failures — the user caused
 * neither. Everything else follows something they just did.
 */
export const SOFTPHONE_ANNOUNCEMENTS = {
  hold: { message: 'Call on hold', politeness: 'polite', topic: 'hold' },
  resume: { message: 'Call resumed', politeness: 'polite', topic: 'hold' },
  mute: { message: 'Microphone muted', politeness: 'polite', topic: 'mute' },
  unmute: {
    message: 'Microphone unmuted',
    politeness: 'polite',
    topic: 'mute',
  },
  reconnecting: {
    message: 'Reconnecting',
    politeness: 'assertive',
    topic: 'connection',
  },
  connectionLost: {
    message: 'Connection lost. Trying to reconnect',
    politeness: 'assertive',
    topic: 'connection',
  },
  connectionRestored: {
    message: 'Reconnected',
    politeness: 'polite',
    topic: 'connection',
  },
  callEnded: { message: 'Call ended', politeness: 'polite', topic: 'call' },
  mergeStarted: {
    message: 'Merging calls',
    politeness: 'polite',
    topic: 'merge',
  },
  transferCompleted: {
    message: 'Call transferred',
    politeness: 'polite',
    topic: 'call',
  },
} as const satisfies Record<string, SoftphoneAnnouncement>;

/** For an anonymous or withheld number. */
const UNKNOWN_CALLER_ANNOUNCEMENT_LABEL = 'unknown caller';

/** The call-view entry announcement. */
export const getCallAnsweredAnnouncement = (
  contact: string | undefined | null
): SoftphoneAnnouncement => ({
  message: `In call with ${contact || UNKNOWN_CALLER_ANNOUNCEMENT_LABEL}`,
  politeness: 'polite',
  topic: 'call',
});

/** What the incoming-call banner has to name for the call to be identifiable. */
export interface IncomingCallParts {
  callerLabel: string;
  /** The queue the call came through, when it came through one. */
  ringGroupName?: string;
  /** Omit when the banner is not showing one — a video invite never does. */
  phoneNumber?: string;
  isVideo?: boolean;
}

/**
 * What an incoming call announces the moment its banner appears.
 *
 * Assertive because it is: the banner carries `role="alert"`. It is the one
 * entry here that does not pass through `AnnouncementService` — the banner
 * renders it into its own live region — but the words and the region they are
 * spoken in belong with every other string the softphone speaks.
 *
 * One sentence, because the banner's subtree keeps moving after it appears and
 * an assertive region restarts on every change. See `SoftphoneCallBanner`.
 */
export const getIncomingCallAnnouncement = ({
  callerLabel,
  ringGroupName,
  phoneNumber,
  isVideo = false,
}: IncomingCallParts): SoftphoneAnnouncement => {
  const lead = isVideo ? 'Incoming video call from' : 'Incoming call from';
  const caller = ringGroupName
    ? `${callerLabel} via ${ringGroupName}`
    : callerLabel;

  return {
    message: phoneNumber
      ? `${lead} ${caller}, ${phoneNumber}`
      : `${lead} ${caller}`,
    politeness: 'assertive',
  };
};

/** A conference always has at least two legs, so the plural is safe. */
export const getMergeCompletedAnnouncement = (
  participantCount: number
): SoftphoneAnnouncement => ({
  message: `Conference call with ${participantCount} participants`,
  politeness: 'polite',
  // Its own topic, not `merge`: the conference is the *result* of the merge,
  // so it must not overwrite "Merging calls" while that is still pending.
  topic: 'conference',
});
